import React, { useEffect, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Upload, Loader2, CheckCircle2, AlertTriangle } from 'lucide-react';
import { Button } from './ui/button';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from './ui/dialog';
import { Progress } from './ui/progress';
import { api } from '../lib/api';
import { useLang } from '../contexts/LanguageContext';

// Fix 34 (2026-09-12): importar el export de órdenes de TikTok Seller Center
// (con Recipient / Phone # / Address Line 1 / Address Line 2 / City completados
// a mano por la contadora) para cargar en lote los datos que la API devuelve
// enmascarados en órdenes con TikTok Shipping. El backend escribe los datos y
// el auto-sync crea los estimados; acá solo se sube el archivo, se muestra el
// preview y se sigue el progreso.

const STATUS_CLASS = {
  ready: 'bg-emerald-500/15 text-emerald-700',
  masked: 'bg-amber-500/15 text-amber-700',
  name_invalid: 'bg-amber-500/15 text-amber-700',
  already_processed: 'bg-sky-500/15 text-sky-700',
  cancelled: 'bg-muted text-muted-foreground',
  not_in_system: 'bg-rose-500/15 text-rose-700',
  before_cutoff: 'bg-muted text-muted-foreground',
  order_id_invalid: 'bg-rose-500/15 text-rose-700',
  order_id_damaged_by_excel: 'bg-rose-500/15 text-rose-700',
  order_id_empty: 'bg-rose-500/15 text-rose-700',
};
const STATUS_ORDER = [
  'ready', 'masked', 'name_invalid', 'not_in_system', 'order_id_damaged_by_excel',
  'order_id_invalid', 'order_id_empty', 'already_processed', 'cancelled', 'before_cutoff',
];
const POLL_MS = 3000;
const POLL_MAX_MS = 10 * 60 * 1000;

export default function TikTokExportImportDialog() {
  const { t } = useLang();
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  // pick | parsing | preview | applying | tracking | done
  const [phase, setPhase] = useState('pick');
  const [preview, setPreview] = useState(null);
  const [status, setStatus] = useState(null);
  const [error, setError] = useState('');
  const fileRef = useRef(null);
  const timerRef = useRef(null);

  const tr = (key, vars = {}) => Object.entries(vars).reduce(
    (s, [k, v]) => s.replace(`{{${k}}}`, String(v)),
    t(`manual_review.${key}`),
  );

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ['manual-review'] });
    qc.invalidateQueries({ queryKey: ['orders'] });
    qc.invalidateQueries({ queryKey: ['dashboard'] });
  };

  const reset = () => {
    clearTimeout(timerRef.current);
    setPhase('pick');
    setPreview(null);
    setStatus(null);
    setError('');
  };

  const onOpenChange = (v) => {
    // No se puede cerrar mientras se escribe o se sigue el progreso.
    if (!v && (phase === 'applying' || phase === 'tracking')) return;
    setOpen(v);
    if (!v) reset();
  };

  const onFile = async (e) => {
    const file = e.target.files && e.target.files[0];
    if (!file) return;
    setError('');
    setPhase('parsing');
    const fd = new FormData();
    fd.append('file', file);
    try {
      const data = (await api.post('/manual-review/tiktok-export-import/preview', fd)).data;
      setPreview(data);
      setPhase('preview');
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setError(typeof detail === 'string' ? detail : tr('import_error'));
      setPhase('pick');
    } finally {
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  const apply = async () => {
    if (!preview) return;
    setError('');
    setPhase('applying');
    try {
      const data = (await api.post(
        `/manual-review/tiktok-export-import/${preview.import_id}/apply`,
      )).data;
      invalidate();
      if (!data.applied) {
        setStatus(data);
        setPhase('done');
        return;
      }
      setPhase('tracking');
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setError(typeof detail === 'string' ? detail : tr('import_error'));
      setPhase('preview');
    }
  };

  useEffect(() => {
    if (phase !== 'tracking' || !preview) return undefined;
    const startedAt = Date.now();
    let cancelled = false;
    const tick = async () => {
      try {
        const d = (await api.get(
          `/manual-review/tiktok-export-import/${preview.import_id}`,
        )).data;
        if (cancelled) return;
        setStatus(d);
        const finished = d.sync_status === 'done' || d.sync_status === 'error'
          || d.sync_status === 'not_needed';
        if (finished || Date.now() - startedAt > POLL_MAX_MS) {
          invalidate();
          setPhase('done');
          return;
        }
      } catch (e) {
        // se reintenta en el próximo tick
      }
      timerRef.current = setTimeout(tick, POLL_MS);
    };
    tick();
    return () => {
      cancelled = true;
      clearTimeout(timerRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phase]);

  const rows = (preview && preview.rows) || [];
  const counts = (preview && preview.counts) || {};
  const readyCount = counts.ready || 0;
  const sortedRows = [...rows].sort(
    (a, b) => STATUS_ORDER.indexOf(a.status) - STATUS_ORDER.indexOf(b.status),
  );
  const progress = (status && status.progress)
    || { applied: 0, processed: 0, pending: 0, cancelled: 0 };
  const pct = progress.applied
    ? Math.round((progress.processed / progress.applied) * 100) : 0;
  const busy = phase === 'applying' || phase === 'tracking';

  return (
    <>
      <Button
        variant="secondary"
        size="sm"
        onClick={() => setOpen(true)}
        data-testid="manual-review-import-button"
      >
        <Upload className="h-4 w-4 mr-2" /> {tr('import_button')}
      </Button>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-4xl" data-testid="manual-review-import-dialog">
          <DialogHeader>
            <DialogTitle>{tr('import_title')}</DialogTitle>
            <DialogDescription>{tr('import_help')}</DialogDescription>
          </DialogHeader>

          {error && (
            <div
              className="rounded-md border border-rose-300 bg-rose-50 p-3 text-[13px] text-rose-800"
              data-testid="manual-review-import-error"
            >
              <AlertTriangle className="inline h-4 w-4 mr-1" /> {error}
            </div>
          )}

          {(phase === 'pick' || phase === 'parsing') && (
            <div className="space-y-3">
              <input
                ref={fileRef}
                type="file"
                accept=".csv,.xlsx,.xlsm,text/csv"
                onChange={onFile}
                disabled={phase === 'parsing'}
                className="block w-full text-[13px]"
                data-testid="manual-review-import-file"
              />
              {phase === 'parsing' && (
                <div className="flex items-center text-[13px] text-muted-foreground">
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" /> {tr('import_parsing')}
                </div>
              )}
              <p className="text-[12px] text-muted-foreground">{tr('import_columns_hint')}</p>
            </div>
          )}

          {(phase === 'preview' || phase === 'applying') && preview && (
            <div className="space-y-3">
              <div className="flex flex-wrap gap-2 text-[12px]" data-testid="manual-review-import-counts">
                {STATUS_ORDER.filter((s) => counts[s]).map((s) => (
                  <span
                    key={s}
                    className={`rounded-full px-2 py-0.5 font-semibold ${STATUS_CLASS[s] || 'bg-muted'}`}
                  >
                    {tr(`import_status_${s}`)}: {counts[s]}
                  </span>
                ))}
              </div>
              <div className="max-h-80 overflow-auto rounded-md border border-border">
                <table className="w-full text-[12px]">
                  <thead className="sticky top-0 bg-muted">
                    <tr className="text-left">
                      <th className="p-2">{tr('import_col_order')}</th>
                      <th className="p-2">{tr('import_col_name')}</th>
                      <th className="p-2">{tr('import_col_phone')}</th>
                      <th className="p-2">{tr('import_col_address')}</th>
                      <th className="p-2">{tr('import_col_status')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {sortedRows.map((r) => (
                      <tr
                        key={`${r.order_id}-${(r.rows || []).join('-')}`}
                        className="border-t border-border align-top"
                        data-testid={`manual-review-import-row-${r.order_id}`}
                      >
                        <td className="p-2 font-mono whitespace-nowrap">
                          {r.order_id}
                          {r.auction ? <span className="ml-1 text-muted-foreground">#{r.auction}</span> : null}
                        </td>
                        <td className="p-2">
                          {r.name || '—'}
                          {r.username ? <div className="text-muted-foreground">@{r.username}</div> : null}
                        </td>
                        <td className="p-2 whitespace-nowrap">{r.phone || '—'}</td>
                        <td className="p-2">
                          {[r.line1, r.line2].filter(Boolean).join(', ') || '—'}
                          <div className="text-muted-foreground">
                            {[r.city, r.state, r.zip].filter(Boolean).join(', ')}
                          </div>
                        </td>
                        <td className="p-2">
                          <span
                            className={`rounded-full px-2 py-0.5 font-semibold whitespace-nowrap ${STATUS_CLASS[r.status] || 'bg-muted'}`}
                          >
                            {tr(`import_status_${r.status}`)}
                          </span>
                          {r.status === 'masked' && r.masked_fields && r.masked_fields.length ? (
                            <div className="text-muted-foreground mt-1">
                              {tr('import_masked_fields')}: {r.masked_fields.map((f) => tr(`import_field_${f}`)).join(', ')}
                            </div>
                          ) : null}
                          {(r.notes || []).map((n) => (
                            <div key={n} className="text-muted-foreground mt-1">{tr(`import_note_${n}`)}</div>
                          ))}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <DialogFooter>
                <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={busy}>
                  {tr('import_cancel')}
                </Button>
                <Button
                  onClick={apply}
                  disabled={!readyCount || busy}
                  data-testid="manual-review-import-apply"
                >
                  {phase === 'applying'
                    ? <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    : <CheckCircle2 className="h-4 w-4 mr-2" />}
                  {readyCount
                    ? tr('import_apply_button', { count: readyCount })
                    : tr('import_nothing_ready')}
                </Button>
              </DialogFooter>
            </div>
          )}

          {(phase === 'tracking' || phase === 'done') && (
            <div className="space-y-3" data-testid="manual-review-import-progress">
              <Progress value={pct} />
              <div className="text-[13px]">
                {phase === 'tracking' ? (
                  <span className="flex items-center">
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    {tr('import_tracking', { done: progress.processed, total: progress.applied })}
                  </span>
                ) : tr('import_done', { done: progress.processed, pending: progress.pending })}
              </div>
              {status && status.sync_summary && status.sync_summary.frozen ? (
                <p className="text-[12px] text-amber-700">{tr('import_frozen')}</p>
              ) : null}
              {status && status.sync_status === 'error' ? (
                <p className="text-[12px] text-rose-700">{tr('import_sync_error')}: {status.sync_error}</p>
              ) : null}
              {phase === 'done' && progress.pending > 0 ? (
                <p className="text-[12px] text-muted-foreground">{tr('import_pending_hint')}</p>
              ) : null}
              {phase === 'done' && (
                <DialogFooter>
                  <Button onClick={() => onOpenChange(false)} data-testid="manual-review-import-close">
                    {tr('import_close')}
                  </Button>
                </DialogFooter>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
